import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, Settings, Trash2, Edit2, Zap, LogOut, Shield, Share2, Users } from 'lucide-react';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import NewProjectForm from './NewProjectForm';
import { ProjectStatus, IndependentProjectStatus } from './ProjectStatus';
import ProjectFilters from './ProjectFilters';
import ProjectDetails from './ProjectDetails';
import ProjectPDFSnapshot from './ProjectPDFSnapshot';
import SecurityDashboard from './SecurityDashboard';
import ShareProjectModal from './ShareProjectModal';
import { listProjects, saveProject, deleteProject, loadProject, subscribeToProjectUpdates, getSharedProjects } from '../services/cloudFirstSync';

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [fullProjectData, setFullProjectData] = useState(null);
  const [showSecurityDashboard, setShowSecurityDashboard] = useState(false);
  const [showPDFSnapshot, setShowPDFSnapshot] = useState(false);
  const [pdfProjectData, setPdfProjectData] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingProject, setSharingProject] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    status: '',
    type: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Load projects from cloud sync service
    const loadProjects = async () => {
      try {
        const [projectsList, sharedProjectsList] = await Promise.all([
          listProjects(),
          getSharedProjects()
        ]);
        setProjects(projectsList);
        setSharedProjects(sharedProjectsList);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();

    // Set up real-time listener for project updates
    const unsubscribe = subscribeToProjectUpdates((updatedProjects) => {
      setProjects(updatedProjects);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleCreateProject = async (projectData) => {
    try {
      console.log('Creating project with data:', projectData);
      console.log('Customer Address:', projectData.customerAddress);
      console.log('Customer Phone:', projectData.customerPhone);
      
      const projectToSave = {
        ...projectData,
        equipment: [],
        conductors: [],
        mapCenter: [-122.4194, 37.7749], // Default to San Francisco
        mapZoom: 13,
        mapStyle: 'streets'
      };
      
      console.log('Project to save:', projectToSave);
      const savedProjectId = await saveProject(projectToSave);
      console.log('Project created with ID:', savedProjectId);

      // Close the form and redirect to the new project
      setShowNewProjectForm(false);
      navigate(`/project/${savedProjectId}`);
    } catch (error) {
      console.error('Error creating project:', error);
      // You might want to show an error message to the user
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        await saveProject({
          ...project,
          status: newStatus
        });

        // Refresh projects list
        const updatedProjects = await listProjects();
        setProjects(updatedProjects);
      }
    } catch (error) {
      console.error('Error updating project status:', error);
      // You might want to show an error message to the user
    }
  };

  const handleSharedProjectStatusChange = async (projectId, newStatus) => {
    try {
      // For shared projects, we'll store the status locally or in a separate collection
      // For now, we'll update the local state to reflect the change
      const updatedSharedProjects = sharedProjects.map(project => 
        project.id === projectId 
          ? { ...project, localStatus: newStatus }
          : project
      );
      setSharedProjects(updatedSharedProjects);
      
      // TODO: In the future, you might want to save this to a separate collection
      // like 'shared_project_statuses' to track individual user's status for shared projects
      console.log(`Updated shared project ${projectId} status to ${newStatus}`);
    } catch (error) {
      console.error('Error updating shared project status:', error);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? This will delete it from both local storage and cloud storage.')) {
      try {
        console.log('Deleting project:', projectId);
        await deleteProject(projectId);
        console.log('Project deleted successfully:', projectId);
        
        // Refresh projects list
        const updatedProjects = await listProjects();
        setProjects(updatedProjects);
        console.log('Projects list refreshed after deletion');
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project: ' + error.message);
      }
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleShareProject = (project) => {
    setSharingProject(project);
    setShowShareModal(true);
  };

  const handleShareSuccess = async () => {
    // Refresh shared projects list
    try {
      const updatedSharedProjects = await getSharedProjects();
      setSharedProjects(updatedSharedProjects);
    } catch (error) {
      console.error('Error refreshing shared projects:', error);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = filters.searchTerm === '' || 
      project.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      project.customer.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      (project.customerAddress && project.customerAddress.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
      (project.customerPhone && project.customerPhone.includes(filters.searchTerm));
    
    const matchesStatus = filters.status === '' || project.status === filters.status;
    const matchesType = filters.type === '' || project.type === filters.type;

    return matchesSearch && matchesStatus && matchesType;
  });

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--accent-primary)] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Navigation Bar */}
      <nav className="bg-[var(--bg-secondary)] shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-[var(--text-primary)]">GridMap</h1>
              <Zap className="text-[var(--accent-primary)] w-6 h-6" />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[var(--text-secondary)]">
                {auth.currentUser?.email}
              </span>
              <button
                onClick={() => setShowSecurityDashboard(true)}
                className="text-[var(--text-primary)] hover:text-[var(--accent-primary)]"
                title="Security Dashboard"
              >
                <Shield size={20} />
              </button>
              <Link
                to="/settings"
                className="text-[var(--text-primary)] hover:text-[var(--accent-primary)]"
              >
                <Settings size={20} />
              </Link>
              <button
                onClick={handleSignOut}
                className="text-[var(--text-primary)] hover:text-[var(--accent-primary)] flex items-center gap-2"
              >
                <LogOut size={20} />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--text-primary)]">
            Welcome, {(auth.currentUser?.displayName?.split(' ')[0]) || 
                     (auth.currentUser?.email?.split('@')[0]?.charAt(0).toUpperCase() + 
                      auth.currentUser?.email?.split('@')[0]?.slice(1)) || 
                     'User'}! 👋
          </h1>
          <p className="text-[var(--text-secondary)] mt-2">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">Your Projects</h2>
          <button
            onClick={() => setShowNewProjectForm(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            New Project
          </button>
        </div>

        {/* Filters */}
        <ProjectFilters
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {/* Project Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.length === 0 ? (
            <div className="col-span-full text-center py-8 text-[var(--text-secondary)]">
              No projects match your filters
            </div>
          ) : (
            filteredProjects.map(project => {
              return (
                <div
                  key={project.id}
                  className="bg-[var(--bg-secondary)] rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-[var(--text-primary)]">
                        {project.name}
                      </h3>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {project.customer}
                      </p>
                      {project.customerAddress && (
                        <p className="text-sm text-[var(--text-secondary)] mt-1">
                          📍 {project.customerAddress}
                        </p>
                      )}
                      {project.customerPhone && (
                        <p className="text-sm text-[var(--text-secondary)] mt-1">
                          📞 {project.customerPhone}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingProject(project)}
                        className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="text-[var(--text-secondary)] hover:text-[var(--danger)]"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-sm text-[var(--text-secondary)]">
                      {project.createdAt ? new Date(project.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : 'N/A'}
                    </span>
                    <ProjectStatus
                      status={project.status}
                      onStatusChange={(newStatus) => handleStatusChange(project.id, newStatus)}
                    />
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Link
                      to={`/project/${project.id}`}
                      className="flex-1 btn btn-primary flex items-center justify-center gap-2 py-1.5 text-sm"
                    >
                      Open Map
                    </Link>
                    <button
                      onClick={async () => {
                        try {
                          console.log('Loading full project data for details:', project.id);
                          const fullProject = await loadProject(project.id);
                          console.log('Full project data loaded:', fullProject);
                          setFullProjectData(fullProject);
                          setSelectedProject(project);
                        } catch (error) {
                          console.error('Error loading project details:', error);
                          // Show error to user and still open with basic data
                          alert('Error loading full project data. Showing basic information only.');
                          setSelectedProject(project);
                        }
                      }}
                      className="flex-1 btn btn-secondary flex items-center justify-center gap-2 py-1.5 text-sm"
                    >
                      Project Details
                    </button>
                    <button
                      onClick={() => handleShareProject(project)}
                      className="flex-1 btn btn-accent flex items-center justify-center gap-2 py-1.5 text-sm"
                      title="Share Project"
                    >
                      <Share2 size={16} />
                      Share
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Shared Projects Section */}
        {sharedProjects.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-6">
              <Users size={24} className="text-[var(--accent-primary)]" />
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">Shared With You</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sharedProjects.map(project => (
                <div
                  key={project.id}
                  className="bg-[var(--bg-secondary)] rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-l-4 border-[var(--accent-primary)]"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-[var(--text-primary)]">
                        {project.name}
                      </h3>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {project.customer}
                      </p>
                      <p className="text-xs text-[var(--accent-primary)] mt-1">
                        Shared by: {project.sharerEmail || project.sharedBy}
                      </p>
                      {project.sharedAt && (
                        <p className="text-xs text-[var(--text-secondary)] mt-1">
                          Shared on: {new Date(project.sharedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-sm text-[var(--text-secondary)]">
                      {project.createdAt ? new Date(project.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : 'N/A'}
                    </span>
                    <IndependentProjectStatus
                      status={project.localStatus || 'new'}
                      onStatusChange={handleSharedProjectStatusChange}
                      projectId={project.id}
                    />
                  </div>

                  <div className="mt-4">
                    <button
                      onClick={async () => {
                        try {
                          setFullProjectData(project);
                          setSelectedProject(project);
                        } catch (error) {
                          console.error('Error loading shared project details:', error);
                          setSelectedProject(project);
                        }
                      }}
                      className="w-full btn btn-secondary flex items-center justify-center gap-2 py-1.5 text-sm"
                    >
                      Project Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* New Project Form Modal */}
      {(showNewProjectForm || editingProject) && (
        <NewProjectForm
          onSubmit={async (formData) => {
            if (editingProject) {
              // Merge all fields from the original project, override with formData
              const updatedProject = { ...editingProject, ...formData };
              await saveProject(updatedProject);
              setEditingProject(null);
            } else {
              await handleCreateProject(formData);
            }
            // Refresh projects list
            const updatedProjects = await listProjects();
            setProjects(updatedProjects);
            setShowNewProjectForm(false);
          }}
          onClose={() => {
            setShowNewProjectForm(false);
            setEditingProject(null);
          }}
          initialData={editingProject}
        />
      )}

      {/* Project Details Modal */}
      {selectedProject && (
        <ProjectDetails
          project={fullProjectData || selectedProject}
          onClose={() => {
            setSelectedProject(null);
            setFullProjectData(null);
          }}
        />
      )}

      {/* Security Dashboard Modal */}
      {showSecurityDashboard && (
        <SecurityDashboard
          onClose={() => setShowSecurityDashboard(false)}
        />
      )}

      {/* PDF Snapshot Modal */}
      {showPDFSnapshot && pdfProjectData && (
        <ProjectPDFSnapshot
          project={pdfProjectData}
          onClose={() => {
            setShowPDFSnapshot(false);
            setPdfProjectData(null);
          }}
        />
      )}

      {/* Share Project Modal */}
      {showShareModal && sharingProject && (
        <ShareProjectModal
          project={sharingProject}
          onClose={() => {
            setShowShareModal(false);
            setSharingProject(null);
          }}
          onShareSuccess={handleShareSuccess}
        />
      )}
    </div>
  );
};

export default Dashboard; 